import SymbolLayer from './SymbolLayer';
import Util from '../utils/util';
import Render from '../render/Render';

/**
 * 矢量瓦片图层
 * 数据以瓦片为单位请求和处理，都绘制在一张画布上
 */
export default class VectorTileLayer extends SymbolLayer {
    /**
     * 构造函数
     * @param {string} id 图层ID
     * @param {Object} options 可选项
     */
    constructor(id, options) {
        super(id, {
            pane: 'tilePane',
            zIndex: 2,
            ...options
        });

        /**
         * 瓦片集合对象
         * @type {object}
         */
        this.tiles = {};
    }

    /**
     * 将图层添加到地图对象上
     * @param {Object} map 地图对象
     */
    beforeAdd(map) {
        if (super.beforeAdd) {
            super.beforeAdd(map);
        }

        let sourceName;
        if (!this.options.source) {
            throw new Error('未配置数据源: source');
        } else if (Util.isString(this.options.source)) {
            sourceName = this.options.source;
        } else {
            sourceName = this.options.source.name;
            this._mergeParameters = this.options.source.mergeParameters;
        }
        this._source = map.SourceController.getSource(sourceName);
        if (!this._source) {
            throw new Error(`数据源不存在: ${sourceName}`);
        }

        if (!this.options.render) {
            throw new Error('未配置渲染器: render');
        } else if (!Render.is(this.options.render)) {
            throw new Error(`不能识别的渲染器: ${this.options.render}`);
        }
    }

    /**
     * 将图层从地图对象中删除时触发
     * @param {object} map 地图对象
     */
    onRemove(map) {
        for (const tile of Object.values(this.tiles)) {
            this._removeTile(tile);
        }

        super.onRemove(map);
    }

    /**
     * 绘制图层内容
     */
    _draw() {
        const newTiles = this._map.TileManager.getTiles(this.options.tileSize);

        for (const [name, tile] of Object.entries(this.tiles)) {
            if (newTiles.hasOwnProperty(name)) {
                // 依然在视口内的瓦片重新绘制
                this._drawTile(tile);
            } else {
                // 不在视口内的瓦片要删除
                this._removeTile(tile);
            }
        }

        // 新加入视口内的瓦片
        for (const [name, tile] of Object.entries(newTiles)) {
            if (!this.tiles.hasOwnProperty(name)) {
                this._addTile(tile);
            }
        }
    }

    /**
     * 增加新瓦片，加载数据后进行绘制
     * @param {object} tile 瓦片对象
     */
    _addTile(tile) {
        tile._status = 0; // 0: 数据未加载；1：数据已加载，但符号的图片未加载；2：数据和符号的图片都加载完成
        tile.mergeParameters = this._mergeParameters;

        this.tiles[tile.name] = tile;

        this._source
            .loadTile(tile)
            .then(data => {
                this._tileLoaded(tile, data);

                this._drawTile(tile);
            })
            .catch(e => {
                // 请求报错时，不做处理，不影响地图渲染
                // console.log(e);
            });
    }

    /**
     * 绘制瓦片
     * @param {object} tile 瓦片对象
     */
    _drawTile(tile) {
        if (tile._status === 2) { // 符号依赖的图片资源已加载
            this._redrawTile(tile, true);
        } else if (tile._status === 1) { // 数据加载完成
            this._loadDependencyResource(tile._symbols).then(() => { // 加载符号依赖的图片资源
                tile._status = 2;

                this._redrawTile(tile, true);
            });
        }
    }

    /**
     * 删除瓦片，同时终止数据请求
     * @param {object} tile 瓦片对象
     */
    _removeTile(tile) {
        this._source.removeTile(tile);

        if (tile._raf) {
            window.cancelAnimationFrame(tile._raf);
        }

        delete this.tiles[tile.name];
    }

    /**
     * 瓦片的数据请求成功后，处理数据，过滤、生成数据模型，以及要渲染的符号
     * @param {object} tile 瓦片对象
     * @param {object} data 数据源返回的数据
     */
    _tileLoaded(tile, data) {
        const symbols = [];

        let features = [];
        if (Util.isFunction(this.options.filter)) {
            features = this.options.filter(data, tile);
        }

        for (const feat of features) {
            symbols.push(...this._symbolizeFeature(feat, this.options.render));
        }

        tile.data = features;
        tile._symbols = symbols;
        tile._renderCounter = 0;
        tile._status = 1;
    }

    /**
     * 重新绘制瓦片上的所有符号
     * @param {object} tile 瓦片对象
     */
    _redrawTile(tile, hard = false) {
        const step = 800;
        if (hard) {
            tile._renderCounter = 0;
            if (tile._raf) {
                window.cancelAnimationFrame(tile._raf);
            }
        }

        const idx = tile._renderCounter * step;
        if (idx < tile._symbols.length) {
            const symbols = tile._symbols.slice(idx, idx + step);
            this._drawSymbols(symbols, tile);

            tile._renderCounter++;

            tile._raf = window.requestAnimationFrame(() => {
                this._redrawTile(tile);
            });
        }
    }

    /**
     * 使用渲染器对象生成数据要渲染的符号
     * @param {object} feature 数据对象
     * @param {object} render 渲染器对象
     * @returns {array<object>} 符号列表
     */
    _symbolizeFeature(feature, render) {
        const symbols = [];
        const symbol = render.getSymbol(feature, this._map.getZoom());
        feature.markRender(render);
        if (symbol) {
            if (Util.isArray(symbol)) {
                symbols.push(...symbol);
            } else {
                symbols.push(symbol);
            }
        }

        symbols.forEach(sl => {
            sl.setResourceFactory(this._map.ImageLoader);
        });

        return symbols;
    }
}
